import React, { useState } from 'react';
import CenteredModal from 'components/UI/Modal/CenteredModal';
import Button from 'components/UI/V3/Button/Button';
import { useEmailMarketing } from '../EmailMarketingProvider';
import { EmailTemplate } from '../types';
import { LocationsChecklist } from '../UI/LocationsChecklist/LocationsChecklist';
import styles from './CopyToLocationsModal.module.scss';

interface CopyToLocationsModalProps {
    template: EmailTemplate;
    onClose: () => void;
}

// Copies a template (location or gallery) into the selected locations — EmailTemplate#duplicate_for per location.
export const CopyToLocationsModal: React.FC<CopyToLocationsModalProps> = ({ template, onClose }) => {
    const { locations, copyTemplateToLocations, locationName } = useEmailMarketing();
    const [selectedIds, setSelectedIds] = useState<Array<number>>([]);
    const isGallery = template.source === 'gallery';

    return (
        <CenteredModal
            isOpen
            onClose={onClose}
            maxWidth="medium"
        >
            <CenteredModal.Header title={isGallery ? 'Add template to locations' : 'Copy to locations'} />
            <CenteredModal.Content padding="large">
                <div className={styles.content}>
                    <p className={styles.text}>
                        {isGallery
                            ? `Each selected location gets its own editable copy of “${template.name}”.`
                            : `Copy “${template.name}” from ${locationName(
                                  template.locationId
                              )} to other locations. Each copy can be edited independently.`}
                    </p>

                    <div className={styles.list}>
                        <LocationsChecklist
                            locations={locations}
                            selectedIds={selectedIds}
                            isDisabled={location => location.id === template.locationId}
                            onChange={setSelectedIds}
                        />
                    </div>

                    <div className={styles.actions}>
                        <Button
                            variant="outlined"
                            label="Cancel"
                            onClick={onClose}
                        />
                        <Button
                            variant="black"
                            label={
                                selectedIds.length
                                    ? `Copy to ${selectedIds.length} location${selectedIds.length === 1 ? '' : 's'}`
                                    : 'Copy'
                            }
                            disabled={selectedIds.length === 0}
                            onClick={() => {
                                copyTemplateToLocations(template, selectedIds);
                                onClose();
                            }}
                        />
                    </div>
                </div>
            </CenteredModal.Content>
        </CenteredModal>
    );
};

export default CopyToLocationsModal;
